import { useMemo } from "react";
import {
  Card,
  Typography,
  Divider,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  Alert,
} from "antd";
import {
  CrownOutlined,
  UserOutlined,
  CalendarOutlined,
  PoundOutlined,
} from "@ant-design/icons";
import { StyledCurrentSubscription } from "./styles";
import TransactionTable from "./TransactionTable";
import ChangePlanModal from "./ChangePlanModal";
import { getSubscriptionDetails } from "../helper";
import CancelPlanModal from "./CancelPlanModal";
import { useSelector } from "react-redux";
import { selectPlanNote } from "../../../../store/user/selectors";
const { Title, Text } = Typography;

const CurrentSubscription = ({ currentPlan }) => {
  const planNote = useSelector(selectPlanNote);
  const { perSeat, selectedPlan } = useMemo(() => {
    return getSubscriptionDetails(currentPlan);
  }, [currentPlan]);

  const formatAmount = (amount) => `£${parseFloat(amount) / 100}`;

  const { perSeatUnitPrice, perSeatTotal, planTotal, grandTotal, isCanceled } =
    useMemo(() => {
      const getAmount = (item) =>
        parseFloat(item?.price?.unit_price?.amount || 0);

      const perSeatUnitPrice = getAmount(perSeat);
      const perSeatTotal = perSeatUnitPrice * (perSeat?.quantity || 0);
      const planTotal = getAmount(selectedPlan);
      const grandTotal = perSeatTotal + planTotal;
      return {
        perSeatUnitPrice,
        perSeatTotal,
        planTotal,
        grandTotal,
        isCanceled: currentPlan?.status === "canceled",
      };
    }, [currentPlan]);

  return (
    <StyledCurrentSubscription className={`${isCanceled ? "canceled" : ""}`}>
      <div className="subscription-header">
        <div className="flex">
          <Title level={4} className="subscription-title">
            {isCanceled
              ? "Previous Subscription (Canceled)"
              : "Current Subscription"}
          </Title>
          <Tag
            className="billing-tag"
            color={isCanceled ? "red" : "blue"}
            icon={<CalendarOutlined />}
          >
            Billed Monthly
          </Tag>
        </div>
        <div className="flex gap">
          <ChangePlanModal isCanceled={isCanceled} />
          {!isCanceled && <CancelPlanModal />}
        </div>
      </div>
      {planNote && (
        <Alert
          title="Subscription Update"
          description={planNote}
          type="info"
          showIcon
          style={{ marginBottom: "10px" }}
        />
      )}
      <Row gutter={20}>
        <Col xs={24} md={8}>
          {selectedPlan && (
            <Card className="plan-card enterprise-card" variant="borderless">
              <div className="card-icon-row">
                <div className="card-icon enterprise-icon">
                  <CrownOutlined />
                </div>
                <Tag className="plan-type-tag enterprise-tag">
                  Selected Plan
                </Tag>
              </div>
              <Title level={5} className="plan-name">
                {selectedPlan.name}
              </Title>
              <Text className="plan-description">
                {selectedPlan.description}
              </Text>
              <Divider className="card-divider" />
              <div className="price-row">
                <Text className="price-label">Monthly Price</Text>
                <Text className="price-value enterprise-price">
                  {formatAmount(planTotal)}
                  <span className="price-period">/mo</span>
                </Text>
              </div>
            </Card>
          )}

          {perSeat && (
            <Card className="plan-card seat-card" variant="borderless">
              <div className="card-icon-row">
                <div className="card-icon seat-icon">
                  <UserOutlined />
                </div>
                <Tag className="plan-type-tag seat-tag">Per Advisor</Tag>
              </div>
              <Title level={5} className="plan-name">
                {perSeat.name}
              </Title>
              <Text className="plan-description">{perSeat.description}</Text>
              <Divider className="card-divider" />
              <div className="seat-breakdown">
                <div className="seat-detail-row">
                  <Text className="price-label">Price per advisor</Text>
                  <Text className="seat-unit-price">
                    {formatAmount(perSeatUnitPrice)}
                  </Text>
                </div>
                <div className="seat-detail-row">
                  <Text className="price-label">Number of advisors</Text>
                  <Text className="seat-quantity">× {perSeat.quantity}</Text>
                </div>
              </div>
              <Divider className="card-divider" />
              <div className="price-row">
                <Text className="price-label">Seats Total</Text>
                <Text className="price-value seat-price">
                  {formatAmount(perSeatTotal)}
                  <span className="price-period">/mo</span>
                </Text>
              </div>
            </Card>
          )}

          <Card className="total-card" variant="borderless">
            <Row align="middle" justify="space-between" gutter={[16, 16]}>
              <Col xs={24} sm={14}>
                <Space direction="vertical" size={2}>
                  <Text className="total-label">Monthly Total</Text>
                  <Text className="total-breakdown">
                    {selectedPlan && `${formatAmount(planTotal)} (plan)`}
                    {selectedPlan && perSeat && " + "}
                    {perSeat &&
                      `${formatAmount(perSeatUnitPrice)} × ${perSeat.quantity} advisors`}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={10} className="total-amount-col">
                <Statistic
                  value={grandTotal}
                  prefix={<PoundOutlined />}
                  suffix={<span className="total-suffix">/month</span>}
                  className="grand-total-stat"
                  //@ts-ignore
                  formatter={(val) => parseFloat(val) / 100}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <TransactionTable />
      </Row>
    </StyledCurrentSubscription>
  );
};
export default CurrentSubscription;
